import React, { useState } from 'react';
import { SafetyIndicator, IndicatorType, AlertLevel } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface SPICatalogTableProps {
  indicators: SafetyIndicator[];
  loading: boolean;
  filterType: 'ALL' | IndicatorType;
  onFilterTypeChange: (type: 'ALL' | IndicatorType) => void;
  onOpenCreate: () => void;
  onSelectIndicator: (indicator: SafetyIndicator) => void;
  isSmsUser?: boolean;
  currentUserDept?: string;
  selectedYear?: number;
  onYearChange?: (year: number) => void;
}

export const SPICatalogTable: React.FC<SPICatalogTableProps> = ({
  indicators,
  loading,
  filterType,
  onFilterTypeChange,
  onOpenCreate,
  onSelectIndicator,
  isSmsUser = false,
  currentUserDept,
  selectedYear = new Date().getFullYear(),
  onYearChange,
}) => {
  const [roleFilter, setRoleFilter] = useState<'ALL' | 'OWNED' | 'DATA_PROVIDER' | 'STAKEHOLDER'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'ARCHIVED' | 'ALL'>('ACTIVE');

  const normUserDept = currentUserDept?.trim().toLowerCase();

  const ownedCount = indicators.filter((ind) => {
    const owner = (ind.ownerDepartment || ind.department)?.trim().toLowerCase();
    return Boolean(normUserDept) && owner === normUserDept;
  }).length;

  const providerCount = indicators.filter((ind) => {
    const provider = (ind.dataProviderDepartment || ind.ownerDepartment || ind.department)?.trim().toLowerCase();
    return Boolean(normUserDept) && provider === normUserDept;
  }).length;

  const stakeholderCount = indicators.filter((ind) => {
    const list = (ind.stakeholderDepartments || []).map((d) => d.trim().toLowerCase());
    return Boolean(normUserDept) && list.includes(normUserDept);
  }).length;

  const filteredIndicators = indicators.filter((ind) => {
    if (statusFilter !== 'ALL' && ind.status !== statusFilter) return false;
    if (filterType !== 'ALL' && ind.indicatorType !== filterType) return false;
    if (roleFilter === 'OWNED') {
      const owner = (ind.ownerDepartment || ind.department)?.trim().toLowerCase();
      if (!normUserDept || owner !== normUserDept) return false;
    }
    if (roleFilter === 'DATA_PROVIDER') {
      const provider = (ind.dataProviderDepartment || ind.ownerDepartment || ind.department)?.trim().toLowerCase();
      if (!normUserDept || provider !== normUserDept) return false;
    }
    if (roleFilter === 'STAKEHOLDER') {
      const list = (ind.stakeholderDepartments || []).map((d) => d.trim().toLowerCase());
      if (!normUserDept || !list.includes(normUserDept)) return false;
    }
    return true;
  });

  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(filteredIndicators);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const getAlertBadge = (level?: AlertLevel) => {
    switch (level) {
      case 'NORMAL':
        return <span className="badge-pill badge-success">NORMAL (OK)</span>;
      case 'ALERT_1_SIGMA':
        return <span className="badge-pill badge-warning">1-SIGMA (CAUTION)</span>;
      case 'ALERT_2_SIGMA':
        return <span className="badge-pill badge-warning">2-SIGMA (WARNING)</span>;
      case 'ALERT_3_SIGMA':
        return <span className="badge-pill badge-danger">3-SIGMA (CRITICAL)</span>;
      default:
        return <span className="badge-pill badge-neutral">No Data</span>;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Top Filter and Action Bar */}
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          {/* Indicator Classification Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Type:</span>
            <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
              <button
                onClick={() => onFilterTypeChange('ALL')}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  background: filterType === 'ALL' ? '#0284c7' : '#ffffff',
                  color: filterType === 'ALL' ? '#ffffff' : '#475569',
                  cursor: 'pointer',
                }}
              >
                All ({indicators.length})
              </button>
              <button
                onClick={() => onFilterTypeChange('LEADING')}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  borderLeft: '1px solid #cbd5e1',
                  borderRight: '1px solid #cbd5e1',
                  background: filterType === 'LEADING' ? '#0284c7' : '#ffffff',
                  color: filterType === 'LEADING' ? '#ffffff' : '#475569',
                  cursor: 'pointer',
                }}
              >
                Leading ({indicators.filter((i) => i.indicatorType === 'LEADING').length})
              </button>
              <button
                onClick={() => onFilterTypeChange('LAGGING')}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  background: filterType === 'LAGGING' ? '#0284c7' : '#ffffff',
                  color: filterType === 'LAGGING' ? '#ffffff' : '#475569',
                  cursor: 'pointer',
                }}
              >
                Lagging ({indicators.filter((i) => i.indicatorType === 'LAGGING').length})
              </button>
            </div>
          </div>

          {/* SPI Role Filter: All vs Owned vs Data Provider */}
          {currentUserDept && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Responsibility:</span>
              <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                <button
                  onClick={() => setRoleFilter('ALL')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: 'none',
                    background: roleFilter === 'ALL' ? '#475569' : '#ffffff',
                    color: roleFilter === 'ALL' ? '#ffffff' : '#475569',
                    cursor: 'pointer',
                  }}
                >
                  All Depts
                </button>
                <button
                  onClick={() => setRoleFilter('OWNED')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: 'none',
                    borderLeft: '1px solid #cbd5e1',
                    borderRight: '1px solid #cbd5e1',
                    background: roleFilter === 'OWNED' ? '#0284c7' : '#ffffff',
                    color: roleFilter === 'OWNED' ? '#ffffff' : '#475569',
                    cursor: 'pointer',
                  }}
                  title={`Indicators owned by ${currentUserDept}`}
                >
                  Owned by My Dept ({ownedCount})
                </button>
                <button
                  onClick={() => setRoleFilter('DATA_PROVIDER')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: 'none',
                    borderRight: '1px solid #cbd5e1',
                    background: roleFilter === 'DATA_PROVIDER' ? '#d97706' : '#ffffff',
                    color: roleFilter === 'DATA_PROVIDER' ? '#ffffff' : '#475569',
                    cursor: 'pointer',
                  }}
                  title={`Indicators where ${currentUserDept} is the designated Data Provider`}
                >
                  ⚡ My Dept is Data Provider ({providerCount})
                </button>
                <button
                  onClick={() => setRoleFilter('STAKEHOLDER')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: 'none',
                    background: roleFilter === 'STAKEHOLDER' ? '#7c3aed' : '#ffffff',
                    color: roleFilter === 'STAKEHOLDER' ? '#ffffff' : '#475569',
                    cursor: 'pointer',
                  }}
                  title={`Indicators where ${currentUserDept} is a stakeholder observer`}
                >
                  👀 Stakeholder ({stakeholderCount})
                </button>
              </div>
            </div>
          )}

          {/* Status Filter (Item 14: Archive) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Status:</span>
            <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
              <button
                type="button"
                onClick={() => setStatusFilter('ACTIVE')}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  background: statusFilter === 'ACTIVE' ? '#16a34a' : '#ffffff',
                  color: statusFilter === 'ACTIVE' ? '#ffffff' : '#475569',
                  cursor: 'pointer'
                }}
              >
                Active ({indicators.filter((i) => i.status === 'ACTIVE').length})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('ARCHIVED')}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  borderLeft: '1px solid #cbd5e1',
                  background: statusFilter === 'ARCHIVED' ? '#64748b' : '#ffffff',
                  color: statusFilter === 'ARCHIVED' ? '#ffffff' : '#475569',
                  cursor: 'pointer'
                }}
              >
                Archived ({indicators.filter((i) => i.status === 'ARCHIVED').length})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('ALL')}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  borderLeft: '1px solid #cbd5e1',
                  background: statusFilter === 'ALL' ? '#0f172a' : '#ffffff',
                  color: statusFilter === 'ALL' ? '#ffffff' : '#475569',
                  cursor: 'pointer'
                }}
              >
                All
              </button>
            </div>
          </div>

          {/* Year Filter */}
          {onYearChange && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Year:</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                <button
                  type="button"
                  onClick={() => onYearChange(selectedYear - 1)}
                  title="Previous Year"
                  style={{
                    padding: '6px 8px',
                    fontSize: '11px',
                    border: 'none',
                    background: '#f8fafc',
                    cursor: 'pointer',
                    borderRight: '1px solid #cbd5e1',
                    color: '#475569',
                  }}
                >
                  ◄
                </button>
                <select
                  value={selectedYear}
                  onChange={(e) => onYearChange(Number(e.target.value))}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    border: 'none',
                    background: '#ffffff',
                    color: '#0f172a',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  {[2027, 2026, 2025, 2024, 2023, 2022].map((y) => (
                    <option key={y} value={y}>
                      {y} {y === new Date().getFullYear() ? '(Current)' : '(Historical)'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => onYearChange(selectedYear + 1)}
                  title="Next Year"
                  style={{
                    padding: '6px 8px',
                    fontSize: '11px',
                    border: 'none',
                    background: '#f8fafc',
                    cursor: 'pointer',
                    borderLeft: '1px solid #cbd5e1',
                    color: '#475569',
                  }}
                >
                  ►
                </button>
              </div>
            </div>
          )}
        </div>

        {isSmsUser ? (
          <button
            onClick={onOpenCreate}
            className="btn-primary"
            style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <span>+ New Indicator (SPI)</span>
          </button>
        ) : (
          <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span>* Only SMS personnel can create new indicators.</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filteredIndicators.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>No indicators registered.</div>
        ) : (
          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="code" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Code
                  </SortableTh>
                  <SortableTh field="name" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Indicator Name / Dept
                  </SortableTh>
                  <SortableTh field="indicatorType" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Type
                  </SortableTh>
                  <SortableTh field="monitoringFrequency" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Frequency / Unit
                  </SortableTh>
                  <SortableTh field="currentValue" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Latest Value ({selectedYear})
                  </SortableTh>
                  <SortableTh field="meanValue" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Annual Mean ({selectedYear})
                  </SortableTh>
                  <SortableTh field="targetValue" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Target ({selectedYear})
                  </SortableTh>
                  <th>Alert Limits (1σ / 2σ / 3σ)</th>
                  <SortableTh field="alertLevel" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                    Threshold Status ({selectedYear})
                  </SortableTh>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((ind) => {
                  const indOwner = (ind.ownerDepartment || ind.department)?.trim().toLowerCase();
                  const indProvider = (ind.dataProviderDepartment || ind.ownerDepartment || ind.department)?.trim().toLowerCase();
                  const indStakeholders = (ind.stakeholderDepartments || []).map((d) => d.trim().toLowerCase());
                  const isOwnerForMe = Boolean(normUserDept) && indOwner === normUserDept;
                  const isProviderForMe = Boolean(normUserDept) && indProvider === normUserDept;
                  const isStakeholderForMe = Boolean(normUserDept) && indStakeholders.includes(normUserDept);
                  const canEnterData = !isStakeholderForMe && (isSmsUser || isProviderForMe || isOwnerForMe);

                  return (
                    <tr
                      key={ind.id}
                      onClick={() => onSelectIndicator(ind)}
                      style={{ cursor: 'pointer', opacity: ind.status === 'ARCHIVED' ? 0.75 : 1 }}
                    >
                      <td style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#0284c7' }}>
                        <div>{ind.code}</div>
                        {ind.status === 'ARCHIVED' && (
                          <span style={{ fontSize: '10px', color: '#991b1b', background: '#fee2e2', padding: '1px 4px', borderRadius: '3px', fontWeight: 700 }}>
                            ARCHIVED
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{ind.name}</span>
                          <span style={{ fontSize: '10px', color: '#1e40af', backgroundColor: '#eff6ff', padding: '1px 5px', borderRadius: '4px', fontWeight: 600 }}>
                            {ind.thresholdMethodology === 'SPECIFIC_TARGET' ? '🎯 Target' : '📐 3σ'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            Owner: {ind.ownerDepartment || ind.department || 'General'}
                            {isOwnerForMe && ' (Your Dept)'}
                          </span>
                          <span
                            style={{
                              fontSize: '11px',
                              color: isProviderForMe ? '#92400e' : '#b45309',
                              backgroundColor: isProviderForMe ? '#fef3c7' : '#fffbeb',
                              border: isProviderForMe ? '1px solid #f59e0b' : '1px solid transparent',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              fontWeight: isProviderForMe ? 700 : 600,
                            }}
                          >
                            Provider: {ind.dataProviderDepartment || ind.ownerDepartment || ind.department || 'General'}
                            {isProviderForMe && ' (⚡ You Enter Data)'}
                          </span>
                          {ind.stakeholderDepartments && ind.stakeholderDepartments.length > 0 && (
                            <span style={{ fontSize: '11px', color: '#6d28d9', backgroundColor: '#f5f3ff', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                              Stakeholders: {ind.stakeholderDepartments.join(', ')}
                              {isStakeholderForMe && ' (👀 Observer)'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={ind.indicatorType === 'LEADING' ? 'badge-pill badge-info' : 'badge-pill badge-neutral'}>
                          {ind.indicatorType}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#475569' }}>
                        <div>
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor:
                                (ind.frequency || '').toUpperCase().startsWith('QUART')
                                  ? '#eff6ff'
                                  : (ind.frequency || '').toUpperCase().startsWith('ANNU')
                                  ? '#f5f3ff'
                                  : '#f1f5f9',
                              color:
                                (ind.frequency || '').toUpperCase().startsWith('QUART')
                                  ? '#1d4ed8'
                                  : (ind.frequency || '').toUpperCase().startsWith('ANNU')
                                  ? '#6d28d9'
                                  : '#334155',
                              border: `1px solid ${
                                (ind.frequency || '').toUpperCase().startsWith('QUART')
                                  ? '#bfdbfe'
                                  : (ind.frequency || '').toUpperCase().startsWith('ANNU')
                                  ? '#ddd6fe'
                                  : '#e2e8f0'
                              }`,
                            }}
                          >
                            {(ind.frequency || '').toUpperCase().startsWith('QUART')
                              ? 'Quarterly'
                              : (ind.frequency || '').toUpperCase().startsWith('ANNU')
                              ? 'Annual'
                              : 'Monthly'}
                          </span>
                        </div>
                        <div style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '11px', marginTop: '3px' }}>{ind.unit}</div>
                      </td>
                      <td>
                        {ind.currentValue ? (
                          <div>
                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>
                              {ind.currentValue.calculatedValue}
                            </span>
                            <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '4px', fontFamily: 'monospace' }}>
                              ({ind.currentValue.period})
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>No Data ({selectedYear})</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const validVals = (ind.recentValues || [])
                            .map((v: any) => Number(v.computedValue ?? v.calculatedValue))
                            .filter((v: number) => typeof v === 'number' && !isNaN(v));
                          const annualAvg = ind.annualAverage ?? (validVals.length > 0 ? (validVals.reduce((a: number, b: number) => a + b, 0) / validVals.length) : null);
                          const baselineM = ind.threshold?.baselineMean ?? (ind.threshold as any)?.meanValue;
                          return (
                            <div>
                              {annualAvg !== null && annualAvg !== undefined ? (
                                <div>
                                  <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
                                    {Number(annualAvg).toFixed(2)}
                                  </span>
                                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    {validVals.length} period{validVals.length === 1 ? '' : 's'}
                                    {baselineM !== undefined && baselineM !== null ? ` | μ: ${Number(baselineM).toFixed(2)}` : ''}
                                  </div>
                                </div>
                              ) : baselineM !== undefined && baselineM !== null ? (
                                <div>
                                  <span style={{ fontWeight: 600, color: '#475569', fontSize: '13px' }}>
                                    μ: {Number(baselineM).toFixed(2)}
                                  </span>
                                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>Baseline Mean</div>
                                </div>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {ind.target ? (
                          <div>
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                              {ind.target.targetDirection === 'BELOW' ? '≤ ' : '≥ '}
                              {ind.target.targetValue}
                            </span>
                            <div style={{ color: '#94a3b8', fontSize: '11px' }}>Year {ind.target.targetYear}</div>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>Unspecified</span>
                        )}
                      </td>
                      <td>
                        {ind.threshold ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px', fontFamily: 'monospace' }}>
                            <span style={{ color: '#b45309', fontWeight: 600 }} title="Alert 1 (1-Sigma Caution)">
                              1σ: {ind.threshold.alert1Value ?? '—'}
                            </span>
                            <span style={{ color: '#c2410c', fontWeight: 600 }} title="Alert 2 (2-Sigma Warning)">
                              2σ: {ind.threshold.alert2Value ?? '—'}
                            </span>
                            <span style={{ color: '#dc2626', fontWeight: 700 }} title="Alert 3 (3-Sigma Critical)">
                              3σ: {ind.threshold.alert3Value ?? '—'}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td>
                        {getAlertBadge(ind.currentValue?.alertLevel)}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {canEnterData ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectIndicator(ind);
                            }}
                            className={isProviderForMe && !isSmsUser ? 'btn-primary' : isOwnerForMe && !isSmsUser ? 'btn-primary' : 'btn-secondary'}
                            style={{
                              width: 'auto',
                              padding: '5px 12px',
                              fontSize: '12px',
                              backgroundColor: isProviderForMe && !isSmsUser ? '#d97706' : isOwnerForMe && !isSmsUser ? '#0284c7' : undefined,
                              borderColor: isProviderForMe && !isSmsUser ? '#b45309' : isOwnerForMe && !isSmsUser ? '#0369a1' : undefined,
                              color: (isProviderForMe && !isSmsUser) || (isOwnerForMe && !isSmsUser) ? '#ffffff' : undefined,
                            }}
                          >
                            {isProviderForMe ? 'Enter Data →' : isOwnerForMe ? 'Enter Data & Actions →' : 'Detail & Data →'}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectIndicator(ind);
                            }}
                            className="btn-secondary"
                            style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                          >
                            View Details →
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <TablePagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="indicators"
        />
      </div>
    </div>
  );
};
